import { api } from '../lib/api';

/**
 * Bringing recordings in from Loom. POST a batch of links; each becomes an
 * import the page watches by polling the list until it is done.
 */

export type ImportStatus = 'queued' | 'fetching' | 'uploading' | 'done' | 'failed';

export interface LoomImport {
  id: string;
  source: 'loom';
  sourceId: string;
  sourceUrl: string;
  title: string | null;
  thumbnailUrl: string | null;
  durationSec: number | null;
  status: ImportStatus;
  progress: number;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
  video: { id: string; shareId: string; status: string; title: string } | null;
}

export interface SubmitImportsResult {
  imports: LoomImport[];
  rejected: Array<{ url: string; error: string }>;
}

/** What a Loom link points at, from the public lookup, before importing. */
export interface LoomPreview {
  id: string;
  title: string;
  description: string | null;
  ownerName: string | null;
  createdAt: string | null;
  durationSec: number | null;
  width: number | null;
  height: number | null;
  thumbnailUrl: string | null;
  hasTranscript: boolean;
}

/** `password` is for Looms that ask for one; it applies to every link in the batch. */
export function submitLoomImports(urls: string[], password?: string): Promise<SubmitImportsResult> {
  return api<SubmitImportsResult>('/v1/imports/loom', {
    method: 'POST',
    body: { urls, ...(password ? { password } : {}) },
  });
}

export function fetchImports(): Promise<{ imports: LoomImport[] }> {
  return api<{ imports: LoomImport[] }>('/v1/imports');
}

export function dismissImport(id: string): Promise<{ ok: boolean }> {
  return api<{ ok: boolean }>(`/v1/imports/${id}`, { method: 'DELETE' });
}

export function previewLoom(url: string): Promise<LoomPreview> {
  return api<LoomPreview>(`/v1/tools/loom?url=${encodeURIComponent(url)}`, { auth: 'none' });
}

export function isImportActive(status: ImportStatus): boolean {
  return status === 'queued' || status === 'fetching' || status === 'uploading';
}

/** Loom links out of whatever was pasted: one per line, or several on a line. */
export function loomLinksIn(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const token of text.split(/[\s,;]+/)) {
    const link = token.trim();
    if (!link || !/loom\.com\//i.test(link)) continue;
    if (seen.has(link)) continue;
    seen.add(link);
    out.push(link);
  }
  return out;
}
